import { Col, Container, Row } from "react-bootstrap";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import { useEffect, useState } from "react";
import SectionHeader from "../components/common/SectionHeader";
import HomeTitle from "../components/home/HomeTitle";
import { NavLink, useSearchParams } from "react-router-dom";
import Last10Feed from "../components/home/Last10Feed";
import { RecentActivity } from "../components/home/RecentActivity";
import OnlinePlayersChart from "../components/home/OnlinePlayersChart";
import { Epics } from "../components/epics/Epics";
import LatestEpicRuns from "../components/home/LatestEpicRuns";
import StatCard from "../components/home/StatCard";
import moment from "moment";
import type { DashboardStatsHistory } from "../types/stats";

ChartJS.register(ArcElement, Tooltip, Legend);

const blacklistedTitles = [
  "hourDouble",
  "helpers",
  "imms",
  "shs",
  "createdAt",
  "count",
  "hourTimeRemaining",
  "eventsUnique"
];

const prettifiedTitles: Record<string, string> = {
  campaigns_done: "CPs Done",
  count: "Online",
  helpers: "Helpers",
  hourDouble: "Hour Double",
  imms: "IMMs",
  mob_deaths: "Mob Deaths",
  new_players: "New Players",
  new_remorts: "New Remorts",
  new_superheroes: "New SHs",
  notes_posted: "Notes Posted",
  number_of_logins: "Logins",
  quests_taken: "Quests Taken",
  triv_mobs_killed: "TP Mobs Killed"
};

function prettifyString(str: string): string {
  // Check if the prettified version exists in the prettifiedTitles map
  if (Object.prototype.hasOwnProperty.call(prettifiedTitles, str)) {
    return prettifiedTitles[str];
  } else {
    // If the prettified version doesn't exist, return the original string
    return str;
  }
}

type DashboardStats = {
  campaigns_done: number;
  count: number;
  createdAt: string;
  helpers: number;
  hourDouble: string;
  eventsUnique?: { name: string; date: string }[];
  imms: number;
  mob_deaths: number;
  new_players: number;
  new_remorts: number;
  new_superheroes: number;
  notes_posted: number;
  number_of_logins: number;
  quests_taken: number;
  shs: number;
  triv_mobs_killed: number;
};

function Home() {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>();
  // Loading
  const [loading, setLoading] = useState(true);
  const [statsHistory, setStatsHistory] = useState<DashboardStatsHistory>();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const API_HOST = process.env.REACT_APP_API;

    // fetch data
    const dataFetch = async () => {
      const data = await (
        await fetch(`${API_HOST}public/stats/dashboard`)
      ).json();

      // set state when the data received
      setDashboardStats(data);
      setLoading(false);
    };

    dataFetch();

    // Re-fetch timer
    const timeToFetch = 5 * 60 * 1000;
    const interval = setInterval(() => {
      dataFetch();
    }, timeToFetch);

    return () => clearInterval(interval);
  }, [searchParams]);

  useEffect(() => {
    const API_HOST = process.env.REACT_APP_API;

    // Daily buckets, not live data - fetched once per page load rather than
    // on the 5-minute dashboard-stats poll above.
    const historyFetch = async () => {
      const data = await (
        await fetch(`${API_HOST}public/stats/dashboard/history?months=6`)
      ).json();

      setStatsHistory(data);
    };

    historyFetch();
  }, []);

  // Today's bucket is still filling up, so it would show as a false dip.
  const getSparklineData = (key: string) => {
    const today = new Date().toISOString().slice(0, 10);
    return statsHistory?.buckets
      .filter((b) => b.day !== today)
      .map(
        (b) => (b as unknown as Record<string, number | null | undefined>)[key]
      );
  };

  return (
    <>
      <Container>
        <Row>
          <HomeTitle
            online={dashboardStats?.count}
            shs={dashboardStats?.shs}
            imms={dashboardStats?.imms}
            helpers={dashboardStats?.helpers}
            loading={loading}
            hourDouble={dashboardStats?.hourDouble}
            mobDeaths={dashboardStats?.mob_deaths}
            iceAge={
              dashboardStats?.eventsUnique?.find(
                (event) => event.name === "ice_age"
              )?.date
            }
          />
        </Row>
      </Container>
      {/* Epics */}
      <Container>
        <SectionHeader
          title="EPICS"
          action={
            <NavLink to="/epics" className="section-link">
              Epics calendar &rarr;
            </NavLink>
          }
        />
        <Epics />
      </Container>
      {/* Recently completed epics */}
      <Container>
        <LatestEpicRuns />
      </Container>
      {/* Last 10 feed */}
      <Container>
        <Last10Feed />
      </Container>
      {/* Online players over time */}
      {statsHistory && statsHistory.buckets.length > 1 && (
        <Container>
          <Row>
            <SectionHeader title="PLAYERS ONLINE" />
            <p style={{ color: "rgb(199, 238, 255)", opacity: 0.7 }}>
              Average number of players online each day over the last{" "}
              {statsHistory.months} {statsHistory.months === 1 ? "month" : "months"}.
            </p>
            <OnlinePlayersChart buckets={statsHistory.buckets} />
          </Row>
        </Container>
      )}
      {/* Recent OPK/HC toggles and SetWanteds */}
      <Container>
        <Row>
          <SectionHeader title="RECENT ACTIVITY" />
          <RecentActivity />
        </Row>
      </Container>
      {dashboardStats && (
        <Container>
          <Row>
            <SectionHeader
              title={`LATEST STATS (TAKEN ${moment(dashboardStats.createdAt)
                .fromNow()
                ?.toUpperCase()})`}
            />
            <p className="text-muted small">
              Totals, with the daily change over the last 6 months below.
            </p>
            <div className="stat-grid">
            {Object.entries(dashboardStats).map(([key, val], i) => {
              if (!blacklistedTitles.includes(key)) {
                return (
                  <div key={i}>
                    <StatCard
                      number={val?.toLocaleString() || "NA"}
                      title={prettifyString(key)}
                      sparklineData={getSparklineData(key)}
                    />
                  </div>
                );
              }
              return null;
            })}
            </div>
          </Row>
        </Container>
      )}
    </>
  );
}

export default Home;
